import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Clapperboard, Loader2, Mail, Lock } from "lucide-react";
import { toast } from "sonner";
import { FilmSlateLoader } from "@/components/FilmSlateLoader";

const HERO_BG =
  "https://images.pexels.com/photos/9665187/pexels-photo-9665187.jpeg";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => {
    setRedirecting(true);
    const redirectUrl = window.location.origin + "/dashboard";
    setTimeout(() => {
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }, 1100);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#050505]">
      {redirecting && <FilmSlateLoader message="Opening secure sign-in…" />}
      <div className="relative hidden lg:block grain overflow-hidden">
        <img src={HERO_BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]/30" />
        <div className="relative z-10 h-full flex flex-col justify-end p-14">
          <Clapperboard className="h-10 w-10 text-[#00F0FF] mb-6" />
          <span className="text-xs uppercase tracking-[0.25em] text-[#00F0FF] mb-3">Narrative Vault</span>
          <h2 className="font-display text-4xl font-bold leading-tight max-w-md">
            Turn your footage into recurring revenue.
          </h2>
          <p className="text-white/60 mt-4 max-w-sm">
            Narrative Vault delivers every event as a private, streamable gallery your clients will love.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-sm fade-up">
          <Link to="/" className="flex items-center gap-2 mb-10">
            <span className="grid place-items-center h-9 w-9 rounded-lg bg-[#00F0FF] text-black">
              <Clapperboard className="h-5 w-5" />
            </span>
            <span className="font-display text-xl font-bold">
              Narrative<span className="text-[#00F0FF]">Vault</span>
            </span>
          </Link>

          <h1 className="font-display text-3xl font-bold">Sign in</h1>
          <p className="text-white/50 mt-2 text-sm">Access your creator dashboard.</p>

          <button
            onClick={googleLogin}
            data-testid="google-login-btn"
            className="mt-8 w-full flex items-center justify-center gap-3 bg-white text-black font-medium rounded-full py-3 hover:scale-[1.02] transition-transform duration-300"
          >
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" />
            Continue with Google
          </button>

          <div className="flex items-center gap-4 my-6">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-xs uppercase tracking-widest text-white/40">or</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
              <input
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] transition-colors"
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
              <input
                data-testid="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] transition-colors"
              />
            </div>
            <button
              data-testid="login-submit"
              disabled={loading}
              className="w-full bg-[#00F0FF] text-black font-semibold rounded-full py-3 hover:scale-[1.02] transition-transform duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Sign In
            </button>
          </form>

          <p className="text-sm text-white/50 mt-6 text-center">
            New here?{" "}
            <Link to="/register" className="text-[#00F0FF] hover:underline">
              Create an account
            </Link>
          </p>
          <p className="text-xs text-white/30 mt-4 text-center">
            Demo: demo@eventvault.com / Demo1234!
          </p>
        </div>
      </div>
    </div>
  );
}
